import React from "react";
import styled from 'styled-components';

import { HeroParagraph } from "../theme/typography";
import StandardBlock from "./StandardBlock";
import { LabeledDataRow } from "./LabeledDataRow";
import { LabeledData } from "./LabeledData";
import { StandardContent, StandardContentTop, StandardContentBottom } from "./StandardBlock";
import { ParagraphSmall } from "../theme/typography";
import { Button } from "./Button";

import block1Horizontal from '../images/serval/block-bgrs-horizontal-serval1.webp';
import block1Vertical from '../images/serval/block-bgrs-vertical-serval1.webp';
import breakTestGraph1 from '../images/serval/serval-force-displacement-y.png';
import breakTestGraph2 from '../images/serval/serval-force-displacement-z.png';
import block2Horizontal from '../images/serval/block-bgrs-horizontal-serval2.webp';
import block2Vertical from '../images/serval/block-bgrs-vertical-serval2.webp';
import block3Horizontal from '../images/serval/block-bgrs-horizontal-serval3.webp';
import block3Vertical from '../images/serval/block-bgrs-vertical-serval3.webp';
import block4Horizontal from '../images/serval/block-bgrs-horizontal-serval4.webp';
import block4Vertical1 from '../images/serval/cam-mounts.png';
import block4Vertical2 from	'../images/serval/vtx-mounts.png';
import products from '../data/products.json';

const HeroHeading = styled.h2`
	${(props) => props.theme.typography.headingXL};
	margin: 0 0 1.5rem 0;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.first}) {
		${(props) => props.theme.typography.heading2XL};
		margin: 0 0 2rem 0;
	}
`;

const ContentContainer = styled(StandardContent)`
	@media only screen and (orientation: landscape) {
		max-width: min(calc(50% - 3rem), 32rem);
	}
`;

const PortraitOnly = styled.div`
	@media only screen and (orientation: landscape) {
		display: none;	
	}
`;

const ImageSetContainer = styled.div`
	display: flex;
	flex-direction: row;
	margin: 2rem 0 0 0;
	gap: 2rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.first}) {
		margin: 3rem 0 1.5rem 0;
		gap: 3rem;
		width: 100%;
	}
`;

const Image = styled.img`
	max-width: 100%;
	min-width: 0;
`;

const ImageSetImage = styled(Image)`
    flex: 1 1 50%;
`;

const PortraitFullWidthImage = styled(Image)`
	max-width: calc(100% + 3rem);
	margin: 1rem -1.5rem;
`;

const PortraitFullWidthImageTop = styled(Image)`
	max-width: calc(100% + 3rem);
	margin: -1.75rem -1.5rem 1rem -1.5rem;
`;

const PortraitFullWidthImageBottom = styled(Image)`
	max-width: calc(100% + 3rem);
	margin: 1rem -1.5rem -1.75rem -1.5rem;
`;

const TextLinkDark = styled.a`
	color: ${(props) => props.theme.colors.purple6};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

const TextLinkLight = styled.a`
	color: ${(props) => props.theme.colors.purple4};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

const Addendum = styled(ParagraphSmall)`
	color: ${(props) => props.theme.colors.neutral6};
`;

const ButtonContainerCenter = styled.div`
	display: flex;
	gap: 1.5rem;
	flex-wrap: wrap;
	justify-content: center;
	margin: 1.5rem 0;
`;

const FlexButton = styled(Button)`
	flex: 0 0 auto;
`;

export class ServalTextBlock extends React.Component {

	render() {
		const servalData = products.find((product) => product.id === "serval");
		
		return (
			<>
				<StandardBlock landscapeBg={block1Horizontal} withSecondaryHeader>
					<ContentContainer>
						<HeroHeading>The&nbsp;winner is&nbsp;thinner.</HeroHeading>
						<HeroParagraph>Most frames keep the same thickness along the whole arm. The Serval 2.5's tapered arm is both tougher and lighter.</HeroParagraph>
						<PortraitOnly>
							<PortraitFullWidthImage src={block1Vertical} alt="Simulations on Serval arm versions" />
						</PortraitOnly>
						<HeroParagraph>I ran <TextLinkLight href="https://youtube.com/shorts/aLfzu6NI43o?feature=share">simulations</TextLinkLight> on 15 different arm shapes, and one of the lightest turned out to be the strongest. Hard to believe, right? I <TextLinkLight href="https://youtube.com/shorts/chSzKiImB-w?feature=share">validated</TextLinkLight> it in the lab and turns out, it’s true.</HeroParagraph>
						<ImageSetContainer>
							<ImageSetImage src={breakTestGraph1} alt="Break test graph 1" />
							<ImageSetImage src={breakTestGraph2} alt="Break test graph 2" />
						</ImageSetContainer>
					</ContentContainer>
				</StandardBlock>
				<StandardBlock colorScheme="yellow" alignment="right"landscapeBg={block2Horizontal} withSecondaryHeader>
					<ContentContainer>
						<PortraitOnly>
							<PortraitFullWidthImageTop src={block2Vertical} alt="Serval assembly blueprint" />
						</PortraitOnly>
						<HeroHeading>Practical and easy to build.</HeroHeading>
						<HeroParagraph>The Serval’s wide top plate protects your VTX from costly midair hits. It has plenty of space to build. You can use a capacitor and even a stack saver.</HeroParagraph>
						<HeroParagraph>And yet, a full Serval build with battery is only ~1% heavier than a MS25, Zigzag or LightSwitch Ultra.</HeroParagraph>
						<LabeledDataRow>
							<LabeledData colorContext="yellow" label="Serval build" data="~477 g" description="Frame: 56g"/>
							<LabeledData colorContext="yellow" label="ZigZag build" data="~471 g" description="Frame: 51g"/>
						</LabeledDataRow>
					</ContentContainer>
				</StandardBlock>
				<StandardBlock colorScheme="dark" portraitBg={block3Vertical} landscapeBg={block3Horizontal} portraitGradient landscapeGradient fullHeightOnMobile spaceBetween withSecondaryHeader>
					<ContentContainer>
						<StandardContentTop>
							<HeroHeading>Fully open source.</HeroHeading>
							<HeroParagraph>The best way to buy a Serval is from the <TextLinkDark href="https://tressym.myshopify.com">Tressym Shop,</TextLinkDark> but you can also cut your own.</HeroParagraph>
						</StandardContentTop>
						<StandardContentBottom>
							<HeroParagraph><TextLinkDark href={servalData.onshapeLink} target="_blank">Modify it,</TextLinkDark> remix it, even sell it if you want - just link back to this page!</HeroParagraph>
							<HeroParagraph>This also guarantees fair prices everywhere, forever.</HeroParagraph>
							<Addendum>License: <TextLinkDark href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank">Creative Commons - Attribution - Share Alike</TextLinkDark></Addendum>
						</StandardContentBottom>
					</ContentContainer>
				</StandardBlock>
				<StandardBlock colorScheme="dark" alignment="center" landscapeBg={block4Horizontal} withSecondaryHeader>
					<ContentContainer>
						<PortraitOnly>
							<PortraitFullWidthImageTop src={block4Vertical1} alt="Serval camera mounts" />
						</PortraitOnly>
						<HeroHeading>Fits all cameras and antennas we could think&nbsp;of.</HeroHeading>
						<HeroParagraph>Something missing?<br/>Maybe I can design it for you.</HeroParagraph>
						<HeroParagraph>All <TextLinkDark href="https://cad.onshape.com/documents/5b897f8c273d2267b0e69daa/w/56afdf9fe20f8e0399798d0e/e/053eaa65c95e0e23b5744ec2?renderMode=0&uiState=69485ba7ee825f9542f86074">camera mounts,</TextLinkDark> <TextLinkDark href="https://cad.onshape.com/documents/c120108bcf51d8e48830a458/w/eb2927a986c39b49ce841395/e/694cb2ae02b0ec8308e3eb27?renderMode=0&uiState=69485bfa45768dc50caa1ed0">VTX mounts</TextLinkDark> and <TextLinkDark href="https://cad.onshape.com/documents/b232c00bed2c0f1a9f934b41/w/b1aed0d5f4e89a9bc69984d0/e/38705fac0772bdce0ae68a66?renderMode=0&uiState=69485c14c5231a76683111a6">fins</TextLinkDark> are open-source, editable, parametric Onshape files. Change them however you want. Go nuts.</HeroParagraph>
						<ButtonContainerCenter>
							<FlexButton colorContext="dark" variant="outlined" label="See all Serval parts" target="_blank" link="https://www.printables.com/model/1425745-tpu-parts-for-the-tressym-serval-5-inch-racing-dro"/>
						</ButtonContainerCenter>
						<PortraitOnly>
							<PortraitFullWidthImageBottom src={block4Vertical2} alt="Serval VTX antenna mounts" />
						</PortraitOnly>
					</ContentContainer>
				</StandardBlock>
			</>
		);
	}
}